import { IconName } from '@ardoq/icons';
import { DEGREES_OF_RELATIONSHIP } from '@ardoq/global-consts';
import { type TraversalViewSettings } from '@ardoq/graph';
import { ViewIds } from '@ardoq/api-types';
import type {
  AggregatedReferenceType,
  OnViewSettingsUpdate,
} from '@ardoq/data-model';
import { DropdownItem, DropdownOptionType } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { isTruthy } from '@ardoq/common-helpers';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { createDropdownSliderOptionManager } from '../settingsHelper';
import { getReferenceTypeDropdownOptions } from '../dropdownOptionUtils/dropdownOptionUtils';
import { MenuMetaOptions } from '../dropdownOptionUtils/types';
import { DropdownConfig, SettingsType } from '@ardoq/view-settings';
import { DEGREES_OF_RELATIONSHIP_SLIDER_MENU_ITEM_ID } from '../consts';
import { type I18nLocale, t } from '@ardoq/i18n';

type TraversalMenuOptions = {
  degreesOfSingleDirection: boolean;
  degreesOfBothDirections: boolean;
  degreesRowAdditionalContent?: React.ReactNode;
  selectReferenceTypes: boolean;
  selectParentChildReferenceTypes: boolean;
  metaOptions: MenuMetaOptions;
};

type TraversalMenuArgs = {
  viewId: ViewIds;
  viewSettings: TraversalViewSettings;
  traversedOutgoingReferenceTypes: AggregatedReferenceType[];
  traversedIncomingReferenceTypes: AggregatedReferenceType[];
  referenceTypes: AggregatedReferenceType[];
  maximumDepth: number;
  options?: TraversalMenuOptions;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  quickPerspectiveReferenceTypeFilters?: Set<string> | null | undefined;
  i18nLocale: I18nLocale;
};

const degreesOfSingleDirectionDropdownSliders = ({
  viewSettings: {
    incomingDegreesOfRelationship,
    outgoingDegreesOfRelationship,
  },
  maximumDepth,
  getSliderOption,
  i18nLocale,
}: Pick<TraversalMenuArgs, 'viewSettings' | 'maximumDepth'> & {
  getSliderOption: ReturnType<typeof createDropdownSliderOptionManager>;
  i18nLocale: I18nLocale;
}) => [
  getSliderOption({
    name: 'incomingDegreesOfRelationship',
    label: t(i18nLocale, 'settingsBar:incoming', 'Incoming'),
    defaultValue: incomingDegreesOfRelationship,
    min: 0,
    max: Math.min(10, maximumDepth),
    step: 1,
    type: DropdownOptionType.SLIDER,
  }),
  getSliderOption({
    name: 'outgoingDegreesOfRelationship',
    label: t(i18nLocale, 'settingsBar:outgoing', 'Outgoing'),
    defaultValue: outgoingDegreesOfRelationship,
    min: 0,
    max: Math.min(11, maximumDepth),
    step: 1,
    substituteMax: Math.min(99, maximumDepth),
    type: DropdownOptionType.SLIDER,
  }),
];
const DEFAULT_TRAVERSAL_MENU_OPTIONS = {
  degreesOfSingleDirection: true,
  degreesOfBothDirections: true,
  selectReferenceTypes: true,
  selectParentChildReferenceTypes: true,
  metaOptions: MenuMetaOptions.ALL_AND_NONE,
};
export const dropdownTraversalMenu = ({
  viewId,
  viewSettings,
  traversedOutgoingReferenceTypes,
  traversedIncomingReferenceTypes,
  maximumDepth,
  options: {
    degreesOfSingleDirection,
    degreesOfBothDirections,
    degreesRowAdditionalContent,
    selectReferenceTypes,
    selectParentChildReferenceTypes,
    metaOptions,
  } = DEFAULT_TRAVERSAL_MENU_OPTIONS,
  onViewSettingsUpdate,
  quickPerspectiveReferenceTypeFilters,
  i18nLocale,
}: TraversalMenuArgs): DropdownConfig => {
  const getSliderOption = createDropdownSliderOptionManager(
    viewId,
    new Map(),
    onViewSettingsUpdate
  );

  const [availableOutgoingRefTypes, availableIncomingRefTypes] = [
    traversedOutgoingReferenceTypes,
    traversedIncomingReferenceTypes,
  ].map(traversedReferenceTypes =>
    traversedReferenceTypes.map(({ name }) => name)
  );

  const degreesOfSingleDirectionInSubSection =
    degreesOfSingleDirection && degreesOfBothDirections;
  const degreesOfSingleDirectionInMainSection =
    degreesOfSingleDirection && !degreesOfBothDirections;
  const degreesOfSingleDirectionItem: DropdownItem[] =
    degreesOfSingleDirectionInSubSection
      ? [
          {
            name: 'expandableTraversalOptions',
            type: DropdownOptionType.EXPANDABLE_SECTION,
            forceExpand:
              viewSettings.incomingDegreesOfRelationship > 0 ||
              viewSettings.outgoingDegreesOfRelationship > 0,
            options: [
              {
                type: DropdownOptionType.LINK,
                link: KnowledgeBaseLink.GRAPH_TRAVERSAL,
              },
              {
                label: t(
                  i18nLocale,
                  'settingsBar:adjustDegreeSingleDirection',
                  'Adjust degree of single direction:'
                ),
                type: DropdownOptionType.MESSAGE,
              },
              ...degreesOfSingleDirectionDropdownSliders({
                viewSettings,
                maximumDepth,
                getSliderOption,
                i18nLocale,
              }),
            ],
          },
        ]
      : degreesOfSingleDirectionInMainSection
        ? [
            {
              label: (
                <span>
                  {DEGREES_OF_RELATIONSHIP}
                  {degreesRowAdditionalContent}
                </span>
              ),
              type: DropdownOptionType.MESSAGE,
            },
            ...degreesOfSingleDirectionDropdownSliders({
              viewSettings,
              maximumDepth,
              getSliderOption,
              i18nLocale,
            }),
          ]
        : [];

  return {
    id: DEGREES_OF_RELATIONSHIP_SLIDER_MENU_ITEM_ID,
    label: DEGREES_OF_RELATIONSHIP,
    iconName: IconName.ACCOUNT_TREE,
    type: SettingsType.DROPDOWN,
    isKeepOpen: true,
    dropdownSize: DropdownSize.M,
    options: [
      degreesOfBothDirections &&
        getSliderOption({
          name: 'depthOfRelationship',
          label: DEGREES_OF_RELATIONSHIP,
          defaultValue: viewSettings.depthOfRelationship,
          min: 0,
          max: Math.min(11, maximumDepth),
          step: 1,
          substituteMax: Math.min(99, maximumDepth),
          type: DropdownOptionType.SLIDER,
        }),
      ...degreesOfSingleDirectionItem,
      {
        type: DropdownOptionType.DIVIDER,
      },
      ...(selectReferenceTypes
        ? getReferenceTypeDropdownOptions({
            viewId,
            availableOutgoingRefTypes,
            availableIncomingRefTypes,
            viewState: viewSettings,
            isParentRelationAsReference: selectParentChildReferenceTypes,
            metaOptions,
            onViewSettingsUpdate,
            quickPerspectiveReferenceTypeFilters,
            i18nLocale,
          })
        : []),
    ].filter(isTruthy),
  };
};
